import { useMemo, useState } from "react";
import {
  Activity, CheckCircle2, ChevronDown, ChevronUp, CloudLightning, CloudRain, CloudSnow,
  Film, PackageCheck, Radar as RadarIcon, Truck, Wind,
} from "lucide-react";
import { money } from "@/lib/format";

const relTime = (iso) => {
  const ms = Date.now() - Date.parse(iso);
  if (!isFinite(ms)) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const riskIcon = (label = "") => {
  const l = label.toLowerCase();
  if (l.includes("thunder")) return CloudLightning;
  if (l.includes("snow") || l.includes("freezing")) return CloudSnow;
  if (l.includes("wind")) return Wind;
  return CloudRain;
};

export default function OpsHud({ routes, risks, radarOn, onToggleRadar, cinematic, onToggleCinematic, onFocus, totalWon }) {
  const [open, setOpen] = useState(true);
  const stats = useMemo(() => {
    const s = { transit: 0, booked: 0, delivered: 0 };
    routes.forEach((r) => { if (s[r.stage] !== undefined) s[r.stage] += 1; });
    return s;
  }, [routes]);

  const feed = useMemo(() => {
    const ev = [];
    routes.forEach((rt) => {
      if (rt.booked_at) ev.push({ type: "booked", at: rt.booked_at, rt });
      if (rt.delivered_at) ev.push({ type: "delivered", at: rt.delivered_at, rt });
    });
    return ev.filter((e) => isFinite(Date.parse(e.at))).sort((a, b) => Date.parse(b.at) - Date.parse(a.at)).slice(0, 7);
  }, [routes]);

  const alerts = useMemo(
    () => (risks || []).filter((r) => r.worst_score > 0).sort((a, b) => b.worst_score - a.worst_score).slice(0, 6),
    [risks],
  );
  const byId = useMemo(() => Object.fromEntries(routes.map((r) => [r.id, r])), [routes]);

  return (
    <div className="absolute right-3 top-3 z-[650] w-72 overflow-hidden rounded-lg border border-white/10 bg-zinc-950/85 text-zinc-100 shadow-2xl backdrop-blur-md" data-testid="ops-hud">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-3 py-2.5 text-left" data-testid="ops-hud-toggle">
        <Activity className="h-4 w-4 text-emerald-400" />
        <span className="font-heading text-sm font-bold tracking-wide">OPS COMMAND</span>
        <span className="ml-auto flex items-center gap-1 text-[10px] font-bold text-emerald-400">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
          LIVE
        </span>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-zinc-500" /> : <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />}
      </button>
      {open && (
        <div className="space-y-3 border-t border-white/10 px-3 py-3">
          <div className="grid grid-cols-3 gap-1.5 text-center">
            {[
              ["In Transit", stats.transit, "text-amber-400", Truck],
              ["Booked", stats.booked, "text-blue-400", PackageCheck],
              ["Delivered", stats.delivered, "text-emerald-400", CheckCircle2],
            ].map(([l, v, c, Icon]) => (
              <div key={l} className="rounded-md bg-white/[0.05] px-1 py-1.5" data-testid={`hud-stat-${l.toLowerCase().replace(" ", "-")}`}>
                <Icon className={`mx-auto h-3.5 w-3.5 ${c}`} />
                <div className={`metric-num text-base font-bold leading-tight ${c}`}>{v}</div>
                <div className="text-[9px] uppercase tracking-wider text-zinc-500">{l}</div>
              </div>
            ))}
          </div>

          <div className="flex gap-1.5">
            <button onClick={onToggleRadar} data-testid="hud-toggle-radar"
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-bold transition-colors ${
                radarOn ? "border-sky-400/60 bg-sky-400/15 text-sky-300" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
              <RadarIcon className="h-3.5 w-3.5" /> Radar {radarOn ? "ON" : ""}
            </button>
            <button onClick={onToggleCinematic} data-testid="hud-toggle-cinematic"
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-md border px-2 py-1.5 text-[11px] font-bold transition-colors ${
                cinematic ? "border-fuchsia-400/60 bg-fuchsia-400/15 text-fuchsia-300" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
              <Film className="h-3.5 w-3.5" /> Ops Wall {cinematic ? "ON" : ""}
            </button>
          </div>

          {alerts.length > 0 && (
            <div data-testid="hud-weather-risks">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">Weather on lanes</div>
              <div className="space-y-1">
                {alerts.map((a) => {
                  const rt = byId[a.id];
                  const side = (a.destination?.score || 0) >= (a.origin?.score || 0) ? a.destination : a.origin;
                  const Icon = riskIcon(side?.label);
                  return (
                    <button key={a.id} onClick={() => rt && onFocus(rt)}
                      className="flex w-full items-center gap-2 rounded-md bg-white/[0.04] px-2 py-1.5 text-left transition-colors hover:bg-white/[0.09]">
                      <Icon className={`h-4 w-4 shrink-0 ${a.worst === "high" ? "text-red-400" : "text-amber-300"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[11px] font-semibold">
                          {rt ? `${rt.origin.state || rt.origin.city} → ${rt.destination.state || rt.destination.city}` : a.quote_number}
                        </div>
                        <div className="truncate text-[10px] text-zinc-500">{side?.label} · {side?.date?.slice(5)}</div>
                      </div>
                      <span className={`rounded px-1 py-0.5 text-[9px] font-bold uppercase ${
                        a.worst === "high" ? "bg-red-500/20 text-red-400" : "bg-amber-400/15 text-amber-300"}`}>{a.worst}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div data-testid="hud-feed">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">Live activity</div>
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {feed.length === 0 && <div className="py-2 text-center text-[11px] text-zinc-600">No recent activity</div>}
              {feed.map((e, i) => (
                <button key={i} onClick={() => onFocus(e.rt)}
                  className="flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-white/[0.07]">
                  {e.type === "delivered"
                    ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" />
                    : <PackageCheck className="h-3.5 w-3.5 shrink-0 text-blue-400" />}
                  <span className="min-w-0 flex-1 truncate text-[11px]">
                    <span className="font-semibold">{e.type === "delivered" ? "Delivered" : "Booked"}</span>
                    <span className="text-zinc-400"> · {e.rt.quote_number} · {e.rt.origin.state} → {e.rt.destination.state}</span>
                  </span>
                  <span className="shrink-0 text-[10px] text-zinc-600">{relTime(e.at)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-white/10 pt-2 text-[11px]">
            <span className="text-zinc-500">Revenue won</span>
            <span className="metric-num font-bold text-emerald-400">{money(totalWon || 0)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
